import type { FlashcardDeck, FlashcardNoteType } from "@japa-reader/contracts";
import { Check, LoaderCircle, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";

type Props = {
  type: FlashcardNoteType;
  setType: (type: FlashcardNoteType | null) => void;
  decks: FlashcardDeck[];
  saving: boolean;
  error: string;
  onClose: () => void;
  onSubmit: () => void;
};

export function NoteTypeDialog({ type, setType, decks, saving, error, onClose, onSubmit }: Props) {
  const [selectedTemplateId, setSelectedTemplateId] = useState(type.templates[0]?.id || "");
  const template = type.templates.find((candidate) => candidate.id === selectedTemplateId) || type.templates[0];
  const updateField = (id: string, changes: Partial<FlashcardNoteType["fields"][number]>) => setType({
    ...type,
    fields: type.fields.map((field) => field.id === id ? { ...field, ...changes } : field),
  });
  const updateTemplate = (id: string, changes: Partial<FlashcardNoteType["templates"][number]>) => setType({
    ...type,
    templates: type.templates.map((candidate) => candidate.id === id ? { ...candidate, ...changes } : candidate),
  });

  function moveField(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= type.fields.length) return;
    const fields = [...type.fields];
    [fields[index], fields[target]] = [fields[target], fields[index]];
    setType({ ...type, fields: fields.map((field, ordinal) => ({ ...field, ordinal })) });
  }

  function moveTemplate(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= type.templates.length) return;
    const templates = [...type.templates];
    [templates[index], templates[target]] = [templates[target], templates[index]];
    setType({ ...type, templates: templates.map((candidate, ordinal) => ({ ...candidate, ordinal })) });
  }

  function addTemplate() {
    if (type.kind === "cloze") return;
    const source = template || type.templates[0];
    const created = {
      id: `template-${crypto.randomUUID()}`,
      name: `Cartão ${type.templates.length + 1}`,
      ordinal: type.templates.length,
      frontTemplate: source?.frontTemplate || `{{${type.fields[0]?.name || "Frente"}}}`,
      backTemplate: source?.backTemplate || "{{FrontSide}}",
      css: source?.css || ".card {}",
      deckOverrideId: null,
    };
    setType({ ...type, templates: [...type.templates, created] });
    setSelectedTemplateId(created.id);
  }

  function removeTemplate(id: string) {
    if (type.kind === "cloze" || type.templates.length <= 1) return;
    const templates = type.templates.filter((candidate) => candidate.id !== id).map((candidate, ordinal) => ({ ...candidate, ordinal }));
    setType({ ...type, templates });
    setSelectedTemplateId(templates[0]?.id || "");
  }

  return <div className="modal-backdrop flash-modal-backdrop" role="presentation">
    <section className="modal-card note-type-dialog" role="dialog" aria-modal="true" aria-labelledby="type-dialog-title">
      <button className="modal-close" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
      <span className="eyebrow">TIPO DE NOTA</span>
      <h2 id="type-dialog-title">Campos e modelos</h2>
      <label><span>Nome</span><input value={type.name} onChange={(event) => setType({ ...type, name: event.target.value })} /></label>

      <div className="type-fields-editor">
        <div className="type-section-heading"><strong>Campos</strong><button onClick={() => setType({ ...type, fields: [...type.fields, { id: `field-${crypto.randomUUID()}`, name: `Campo ${type.fields.length + 1}`, ordinal: type.fields.length, font: "Inter", fontSize: 20, rtl: false, sticky: false }] })}><Plus size={14} /> Campo</button></div>
        {type.fields.map((field, index) => <article className="field-editor-card" key={field.id}>
          <div className="field-editor-main"><span>{index + 1}</span><input aria-label={`Nome do campo ${index + 1}`} value={field.name} onChange={(event) => updateField(field.id, { name: event.target.value })} /><div className="reorder-actions"><button disabled={index === 0} onClick={() => moveField(index, -1)} aria-label="Mover campo para cima">↑</button><button disabled={index === type.fields.length - 1} onClick={() => moveField(index, 1)} aria-label="Mover campo para baixo">↓</button>{type.fields.length > 1 && <button onClick={() => setType({ ...type, fields: type.fields.filter((candidate) => candidate.id !== field.id).map((candidate, ordinal) => ({ ...candidate, ordinal })) })} aria-label="Excluir campo"><Trash2 size={14} /></button>}</div></div>
          <div className="field-editor-options"><label><span>Fonte de edição</span><input value={field.font} onChange={(event) => updateField(field.id, { font: event.target.value })} /></label><label><span>Tamanho</span><input type="number" min="8" max="200" value={field.fontSize} onChange={(event) => updateField(field.id, { fontSize: Number(event.target.value) })} /></label><label className="checkbox-field"><input type="checkbox" checked={field.rtl} onChange={(event) => updateField(field.id, { rtl: event.target.checked })} /><span>Direita para esquerda</span></label><label className="checkbox-field"><input type="checkbox" checked={field.sticky} onChange={(event) => updateField(field.id, { sticky: event.target.checked })} /><span>Campo fixo</span></label></div>
        </article>)}
      </div>

      <div className="template-editor-shell">
        <div className="type-section-heading"><strong>Modelos de cartão</strong>{type.kind === "standard" && <button onClick={addTemplate}><Plus size={14} /> Modelo</button>}</div>
        <div className="template-tabs" role="tablist">{type.templates.map((candidate) => <button key={candidate.id} className={candidate.id === template?.id ? "is-active" : ""} onClick={() => setSelectedTemplateId(candidate.id)} role="tab" aria-selected={candidate.id === template?.id}>{candidate.name}</button>)}</div>
        {template && <div className="template-editor">
          <div className="template-meta-grid"><label><span>Nome do modelo</span><input value={template.name} onChange={(event) => updateTemplate(template.id, { name: event.target.value })} /></label><label><span>Baralho de destino</span><select value={template.deckOverrideId || ""} onChange={(event) => updateTemplate(template.id, { deckOverrideId: event.target.value || null })}><option value="">Usar o baralho da nota</option>{flattenDecks(decks).filter(({ deck }) => deck.kind === "standard").map(({ deck, depth }) => <option value={deck.id} key={deck.id}>{"— ".repeat(depth)}{deck.name}</option>)}</select></label></div>
          <div className="template-order-actions"><button disabled={template.ordinal === 0} onClick={() => moveTemplate(template.ordinal, -1)}>↑ Mover</button><button disabled={template.ordinal === type.templates.length - 1} onClick={() => moveTemplate(template.ordinal, 1)}>↓ Mover</button>{type.kind === "standard" && type.templates.length > 1 && <button className="danger-action" onClick={() => removeTemplate(template.id)}><Trash2 size={14} /> Excluir modelo</button>}</div>
          <label><span>Frente (HTML)</span><textarea value={template.frontTemplate} onChange={(event) => updateTemplate(template.id, { frontTemplate: event.target.value })} rows={5} /></label>
          <label><span>Verso (HTML)</span><textarea value={template.backTemplate} onChange={(event) => updateTemplate(template.id, { backTemplate: event.target.value })} rows={5} /></label>
          <label><span>Estilo (CSS)</span><textarea value={template.css} onChange={(event) => updateTemplate(template.id, { css: event.target.value })} rows={5} /></label>
        </div>}
      </div>
      {error && <div className="form-error">{error}</div>}
      <div className="modal-actions"><button className="ghost-button" onClick={onClose}>Cancelar</button><button className="align-button" disabled={saving} onClick={onSubmit}>{saving ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />} Salvar tipo</button></div>
    </section>
  </div>;
}

function flattenDecks(decks: FlashcardDeck[]) {
  const result: Array<{ deck: FlashcardDeck; depth: number }> = [];
  const visit = (parentId: string | null, depth: number) => decks.filter((deck) => deck.parentId === parentId).sort((left, right) => left.name.localeCompare(right.name)).forEach((deck) => {
    result.push({ deck, depth });
    visit(deck.id, depth + 1);
  });
  visit(null, 0);
  return result;
}
